import React, { createContext, useContext, useEffect, useState } from "react";

/**
 * CaptionsContext — global "captions are ON" preference.
 *
 * One toggle controls every player + every TTS read-aloud across the
 * app. When ON:
 *   • YouTube/embed iframes are loaded with cc_load_policy=1 so
 *     YouTube's own CC track is forced visible from the first frame.
 *   • Cloudinary / Archive.org MP4 <video> elements get a <track>
 *     element pointing at /api/captions/vtt?url=... — generated
 *     on-demand via Whisper and cached server-side.
 *   • The global <LiveCaptions /> overlay reads AudioContext.queue
 *     + currentIndex and renders the currently-spoken text in a
 *     high-contrast band pinned to the bottom of the viewport, so
 *     Bible read-aloud, AI Companion replies, Storybook narration,
 *     and any other TTS-driven feature gets captions too.
 *
 * Persisted to localStorage so the user only flips it once. Default
 * OFF — most users don't need captions and the live overlay would
 * distract them.
 */
const STORAGE_KEY = "hw:captions:on";

const CaptionsContext = createContext({ on: false, toggle: () => {}, setOn: () => {} });

export function CaptionsProvider({ children }) {
  const [on, setOn] = useState(() => {
    if (typeof window === "undefined") return false;
    try {
      return window.localStorage.getItem(STORAGE_KEY) === "1";
    } catch (_) {
      return false;
    }
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(STORAGE_KEY, on ? "1" : "0");
    } catch (_) { /* private mode */ }
    // Broadcast so non-React listeners (vanilla DOM <video> elements
    // that lazily attach a <track>) can react.
    try {
      window.dispatchEvent(new CustomEvent("hw:captions:changed", { detail: { on } }));
    } catch (_) {}
  }, [on]);

  const toggle = () => setOn((v) => !v);

  return (
    <CaptionsContext.Provider value={{ on, toggle, setOn }}>
      {children}
    </CaptionsContext.Provider>
  );
}

export function useCaptions() {
  return useContext(CaptionsContext);
}
